import * as React from "react";

import {
  Box,
  Button,
  ButtonProps,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";

import { useTranslation } from "react-i18next";

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    meeting: true;
    ticket: true;
    device: true;
  }
}

type IconProps = {
  element: React.JSX.Element;
  color?: "default" | "device" | "error" | "meeting";
};

export type DeckboardIconButtonProps = ButtonProps & {
  colorButton?: "default" | "device" | "error" | "meeting" | "primary";
  icon?: IconProps;
  label?: string;
  state?: boolean;
  hidden?: boolean;
  tooltip?: string;
  "data-testid"?: string;
};

const classColorButton = (
  color: "default" | "device" | "error" | "meeting" | "primary",
  theme: Theme,
  state: boolean
) => {
  const build = (backgroundColor: string, hover: string, color?: string) => ({
    "&.MuiButton-root": {
      backgroundColor: !state ? backgroundColor : theme.palette.primary.main,
      color: !state
        ? color ?? theme.palette.common.white
        : theme.palette.background.default,
      "&:hover": {
        backgroundColor: hover,
      },
    },
  });
  switch (color) {
    case "meeting":
      return build(
        theme.palette.meeting.background,
        theme.palette.meeting.dark
      );
    case "error":
      return build(
        theme.palette.ticket.Critical.main,
        theme.palette.ticket.Critical.dark
      );
    case "device":
      return build(theme.palette.device.border, theme.palette.device.dark);
    case "primary":
      return {};
    case "default":
    default:
      return build(
        theme.palette.common.grey.g474a53,
        theme.palette.common.grey.g26272D
      );
  }
};

const DeckboardIconButton = (
  props: React.PropsWithChildren<DeckboardIconButtonProps>
) => {
  const {
    icon,
    colorButton = "default",
    label = "",
    state = false,
    hidden = false,
    tooltip,
    ...other
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Tooltip title={hidden ? "" : (t(tooltip ?? label) as string)} arrow>
      <Box>
        {!hidden ? (
          <Button
            variant="contained"
            sx={[
              {
                "&.MuiButton-root": {
                  borderRadius: "4px",
                  padding: theme.spacing(1),

                  "&.Mui-disabled": {
                    opacity: "50%",
                  },
                  "& svg": {
                    width: theme.spacing(6),
                    height: theme.spacing(6),
                  },
                },
              },
              classColorButton(colorButton, theme, state),
            ]}
            {...other}
          >
            {icon?.element}
          </Button>
        ) : (
          <Box
            sx={(theme) => ({
              width: theme.spacing(6),
              height: theme.spacing(6),
            })}
          ></Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default DeckboardIconButton;
